import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import { DataProviderInstance } from '../../api/DataProvider';
import CustomButton from '../../components/buttons/CustomButton';
import RemotePaths from '../../consts/RemotePaths';
import { getRoleDisplayName } from '../../utils/RoleManager';
import ActiveSessions from './components/ActiveSessions';
import CommunicationPreferences from './components/CommunicationPreferences';
import styles from './Profile.module.css';

const Profile = () => {
  const dataProvider = DataProviderInstance();
  const { UserData, GetUserProfile, UpdateUserProfile, session } = dataProvider;
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [profileData, setProfileData] = useState({
    name: '',
    picture: '',
    createdAt: '',
    email: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const fileInputRef = useRef(null);

  // Load initial user data
  useEffect(() => {
    // Fetch the latest user profile data
    GetUserProfile((response) => {
      if (response.success && response.user) {
        setProfileData({
          name: response.user.profile.name || '',
          picture: response.user.profile.picture || '',
          createdAt: formatDate(response.user.createdAt) || 'N/A',
          email: response.user.email || '',
          phone: response.user.profile.phone || ''
        });
      }
    });
  }, []);

  // Update profile data when UserData changes
  useEffect(() => {
    if (UserData && UserData.profile) {
      setProfileData(prevData => ({
        ...prevData,
        name: UserData.profile.name || '',
        picture: UserData.profile.picture || '',
        createdAt: formatDate(UserData.createdAt) || 'N/A',
        email: UserData.email || '',
        phone: UserData.profile.phone || ''
      }));
    }
  }, [UserData]);

  // Format date to display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Handle profile field changes
  const handleNameChange = (e) => {
    setProfileData({ ...profileData, name: e.target.value });
  };

  const handlePhoneChange = (e) => {
    setProfileData({ ...profileData, phone: e.target.value });
  };

  // Handle password field changes
  const handlePasswordChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
    
    // Clear error when user types
    if (passwordErrors[field]) {
      setPasswordErrors({ ...passwordErrors, [field]: '' });
    }
    
    // Reset success message when user starts typing again
    if (passwordUpdateSuccess) {
      setPasswordUpdateSuccess(false);
    }
  };

  // Handle password visibility toggle
  const handlePasswordVisibilityToggle = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  // Validate password fields
  const validatePasswordFields = () => {
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    let isValid = true;

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'A senha atual é obrigatória';
      isValid = false;
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'A nova senha é obrigatória';
      isValid = false;
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Confirme a nova senha';
      isValid = false;
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
      isValid = false;
    }

    setPasswordErrors(errors);
    return isValid;
  };

  // Handle profile update
  const handleProfileUpdate = async () => {
    setLoading(true);
    
    UpdateUserProfile(profileData.name, profileData.phone, (response) => {
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Perfil atualizado com sucesso!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Erro ao atualizar perfil',
          severity: 'error'
        });
      }
      setLoading(false);
    });
  };

  // Handle profile picture upload
  const handlePictureUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.match('image.*')) {
      setSnackbar({
        open: true,
        message: 'Por favor, selecione uma imagem válida',
        severity: 'error'
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'A imagem deve ter no máximo 5MB',
        severity: 'error'
      });
      return;
    }

    // Set both loading states to disable all inputs
    setLoading(true);
    setUploadingPhoto(true);
    
    dataProvider.UpdateUserPicture(file, (response) => {
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Foto de perfil atualizada com sucesso!',
          severity: 'success'
        });
        
        // Update profile picture in state and force refresh
        setProfileData(prevData => ({
          ...prevData,
          picture: response.picture
        }));
        
        // Force a refresh of the user data
        GetUserProfile((refreshResponse) => {
          if (refreshResponse.success && refreshResponse.user) {
            setProfileData(prevData => ({
              ...prevData,
              picture: refreshResponse.user.profile.picture || ''
            }));
          }
        });
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Erro ao atualizar foto de perfil',
          severity: 'error'
        });
      }
      setLoading(false);
      setUploadingPhoto(false);
    });
  };

  // Handle password update
  const handlePasswordUpdate = async () => {
    if (!validatePasswordFields()) return;

    setLoading(true);
    
    dataProvider.UpdateUserPassword(
      passwordData.currentPassword,
      passwordData.newPassword,
      (response) => {
        if (response.success) {
          // Mostrar mensagem de sucesso ao lado do botão
          setPasswordUpdateSuccess(true);
          
          // Esconder a mensagem após 5 segundos
          setTimeout(() => {
            setPasswordUpdateSuccess(false);
          }, 5000);
          
          // Clear password fields
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        } else {
          // Resetar o estado de sucesso em caso de erro
          setPasswordUpdateSuccess(false);
          // Handle specific error cases
          if (response.status === 401) {
            setPasswordErrors({
              ...passwordErrors,
              currentPassword: 'Senha atual incorreta'
            });
          } else if (response.error && response.error.includes('nova senha não pode ser igual')) {
            setPasswordErrors({
              ...passwordErrors,
              newPassword: 'A nova senha não pode ser igual à senha atual'
            });
          } else if (response.details && Array.isArray(response.details)) {
            // Se temos detalhes específicos sobre validação de senha, mostramos eles
            if (response.details.some(detail => detail.includes('nova senha não pode ser igual'))) {
              setPasswordErrors({
                ...passwordErrors,
                newPassword: 'A nova senha não pode ser igual à senha atual'
              });
            } else {
              // Exibir todas as regras de validação que falharam
              setPasswordErrors({
                ...passwordErrors,
                newPassword: response.details.join('; ')
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: response.error || 'Erro ao atualizar senha',
              severity: 'error'
            });
          }
        }
        setLoading(false);
      }
    );
  };

  // Avisos vindos das seções filhas (ex.: sessões ativas)
  const showFeedback = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className='page-content'>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            ...(snackbar.severity === 'error' && {
              backgroundColor: '#d32f2f',
              color: '#ffffff',
              '& .MuiAlert-icon': {
                color: '#ffffff'
              },
              '& .MuiAlert-action': {
                color: '#ffffff'
              },
              '& .MuiIconButton-root': {
                color: '#ffffff'
              }
            })
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div className={styles.mainContainer}>
        <AreaTitle
          Title="Meu Perfil"
          Icon={<PersonIcon />}
          Description="Visualize e edite suas informações pessoais"
        />
        <Divider sx={{ margin: '16px 0' }} />

        <Grid container spacing={3}>
          {/* Profile Information Section */}
          <Grid item xs={12} md={6}>
            <Paper
              className={styles.section}
              elevation={0}
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
                border: '1px solid #E2E8F0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  Informações Pessoais
                </Typography>
              </Box>
              <Divider sx={{ margin: '16px 0' }} />

              {/* New layout with photo on left, info on right */}
              <Grid container spacing={3}>
                {/* Left side - Profile picture */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={profileData.picture
                        ? RemotePaths.PATH_USER_UPLOADS_USER_PICTURE(UserData.id) + "/" + profileData.picture
                        : null}
                      alt={profileData.name}
                      className={styles.avatar}
                      sx={{
                        width: 120,
                        height: 120,
                        border: '3px solid #f0f0f0',
                        mb: 1
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CustomButton
                        component="label"
                        style="secondary"
                        variant="contained"
                        icon={uploadingPhoto ? null : <PhotoCameraIcon />}
                        label={uploadingPhoto ? "Enviando..." : "Alterar Foto"}
                        disabled={loading || uploadingPhoto}
                        className={styles.uploadButton}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        size="small"
                      >
                      </CustomButton>
                      {uploadingPhoto && (
                        <CircularProgress size={24} sx={{ ml: 1 }} />
                      )}
                    </Box>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handlePictureUpload}
                      style={{ display: 'none' }}
                    />
                  </Box>
                </Grid>

                {/* Right side - Non-editable info */}
                <Grid item xs={12} sm={8}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={profileData.email}
                      margin="normal"
                      variant="outlined"
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Membro desde"
                      value={profileData.createdAt}
                      margin="normal"
                      variant="outlined"
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />

                    {/* Role information with chip highlight */}
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ mr: 1 }}>
                        Função:
                      </Typography>
                      <Chip
                        label={getRoleDisplayName(session?.companies?.find(company => company?.id == session?.currentCompany)?.role?.name || '')}
                        color="primary"
                        variant="outlined"
                        sx={{
                          fontWeight: 'bold',
                          borderRadius: '16px',
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          borderColor: 'rgba(25, 118, 210, 0.5)',
                          padding: '4px',
                          height: 'auto',
                          '& .MuiChip-icon': {
                            color: 'primary.main',
                            marginLeft: '8px'
                          },
                          '& .MuiChip-label': {
                            padding: '4px 8px',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ margin: '24px 0 16px' }} />

              {/* Editable fields below */}
              <Box>
                <TextField
                  fullWidth
                  label="Nome"
                  value={profileData.name}
                  onChange={handleNameChange}
                  margin="normal"
                  variant="outlined"
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label="Telefone"
                  value={profileData.phone}
                  onChange={handlePhoneChange}
                  margin="normal"
                  variant="outlined"
                  disabled={loading}
                />

                <CustomButton
                  style="secondary"
                  variant="contained"
                  icon={<SaveIcon />}
                  label={loading ? "Salvando..." : "Salvar Alterações"}
                  onClick={handleProfileUpdate}
                  disabled={loading}
                  sx={{ mt: 2 }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Password Change Section */}
          <Grid item xs={12} md={6}>
               <Paper
              className={styles.section}
              elevation={0}
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
                border: '1px solid #E2E8F0'
              }}
            >
            
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  Alterar Senha
                </Typography>
              </Box>
              <Divider sx={{ margin: '16px 0' }} />

              <TextField
                fullWidth
                type={showPasswords.currentPassword ? "text" : "password"}
                label="Senha Atual"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={loading}
                error={!!passwordErrors.currentPassword}
                helperText={passwordErrors.currentPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handlePasswordVisibilityToggle('currentPassword')}
                        edge="end"
                        disabled={loading}
                      >
                        {showPasswords.currentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={showPasswords.newPassword ? "text" : "password"}
                label="Nova Senha"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={loading}
                error={!!passwordErrors.newPassword}
                helperText={passwordErrors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handlePasswordVisibilityToggle('newPassword')}
                        edge="end"
                        disabled={loading}
                      >
                        {showPasswords.newPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={showPasswords.confirmPassword ? "text" : "password"}
                label="Confirmar Nova Senha"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={loading}
                error={!!passwordErrors.confirmPassword}
                helperText={passwordErrors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handlePasswordVisibilityToggle('confirmPassword')}
                        edge="end"
                        disabled={loading}
                      >
                        {showPasswords.confirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <CustomButton
                  style="secondary"
                  variant="contained"
                  icon={<LockIcon />}
                  label={loading ? "Atualizando..." : "Atualizar Senha"}
                  onClick={handlePasswordUpdate}
                  disabled={loading}
                />
                
                {passwordUpdateSuccess && (
                  <Alert
                    severity="success"
                    sx={{ ml: 2, flexGrow: 1 }}
                  >
                    Senha atualizada com sucesso!
                  </Alert>
                )}
              </Box>

            </Paper>
          </Grid>

          {/* Comunicações (Novidades da Gabriela por e-mail) */}
          <Grid item xs={12}>
            <CommunicationPreferences onFeedback={showFeedback} />
          </Grid>

          {/* Active Sessions Section */}
          <Grid item xs={12}>
            <ActiveSessions onFeedback={showFeedback} />
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default Profile;